import React, { Fragment, lazy, memo, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { FlagGroup } from '@atlaskit/flag';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { onAIAnswerResultShown } from '../../../ai-answer-dialog/analytics';
import type { commonAttributesType } from '../../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { logger } from '../../../common/utils/logger';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { useDwellTracker } from '../../../common/utils/use-dwell-tracker';
import { type AiBrand, AnswerCardType } from '../../../constants';
import { onKnowledgeCardLinkClick } from '../../../knowledge-cards/analytics';
import { isHello, sha256Hash } from '../../../utils';
import { AIDisclaimerAndFeedback } from '../../common/ui/ai-disclaimer-and-feedback';
import { KnowledgeCard } from '../../knowledge-cards/base-components/KnowledgeCard';

import { AddPinModal } from './add-pin/add-pin-modal';
import { BookmarkState } from './bookmark-api/useBookmarkQuery/fetch-bookmark';
import { BookmarkInvisibleLink } from './bookmark-invisible-link';
import { BookmarkFeedbackModal } from './feedback-modal';
import { messages } from './messages';
import { fetchObjectResolver } from './utils';

const AkFeedbackFlag = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/feedback-flag" */ '@atlaskit/feedback-collector'
	).then(({ FeedbackFlag }) => {
		return {
			default: FeedbackFlag,
		};
	}),
);

const styles = cssMap({
	container: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingBottom: token('space.200'),
		position: 'relative',
	},
	headingUnderline: {
		textDecoration: 'underline',
		textDecorationColor: token('color.text.selected'),
		textDecorationThickness: '1.5px',
	},
});

type BookmarkProps = {
	query: string;
	bookmarkId: string;
	url: string;
	orgId: string;
	cloudId: string;
	hasBookmarkCreationAccess: boolean;
	hasAdminhubAccess: boolean;
	searchSessionId: string;
	source: string;
	brand: AiBrand;
	bookmarkState?: BookmarkState;
	title?: string;
	description?: string;
	userDetails?: {
		name?: string;
		id?: string;
	};
	workspaceId: string;
	product: string;
	requestId: string | null;
};

export const BookmarkKnowledgeCard: React.MemoExoticComponent<
	({
		query,
		bookmarkId,
		url,
		title,
		description,
		orgId,
		cloudId,
		bookmarkState,
		searchSessionId,
		source,
		brand,
		hasBookmarkCreationAccess,
		hasAdminhubAccess,
		userDetails,
		workspaceId,
		product,
		requestId,
	}: BookmarkProps) => React.JSX.Element
> = memo(
	({
		query,
		bookmarkId,
		url,
		title,
		description,
		orgId,
		cloudId,
		bookmarkState = BookmarkState.ACTIVE,
		searchSessionId,
		source,
		brand,
		hasBookmarkCreationAccess,
		hasAdminhubAccess,
		userDetails,
		workspaceId,
		product,
		requestId,
	}: BookmarkProps): React.JSX.Element => {
		const { formatMessage, locale } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { trackAIInteractionInit } = useAIEventsInstrumentation();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

		const [bookmarkDescription, setBookmarkDescription] = useState(description);
		const [hovered, setHovered] = useState(false);
		const [showAddBookmarkModal, setShowAddBookmarkModal] = useState(false);
		const [showFeedbackModal, setShowFeedbackModal] = useState(false);
		const [showFeedbackFlag, setShowFeedbackFlag] = useState(false);
		const cardRef = useRef<HTMLDivElement>(null);
		const initialLoadTs = useRef<number>(Date.now()).current;

		// Dwell tracker for viewport exit detection
		useDwellTracker({
			elementRef: cardRef,
			onDwellEnd: (dwellTime) => {
				fireSAINUsageEvent({
					metCriteria: SainAction.FMP_RESULT_EXITED,
					dwellTime,
					commonAttributes: bookmarkAttributes,
				});
			},
		});

		const bookmarkAttributes: commonAttributesType & {
			bookmarkId: string;
			bookmarkUrl: string;
		} = useMemo(
			() => ({
				cloudId,
				orgId,
				source,
				query,
				queryHash: sha256Hash(query),
				baseQueryHash: sha256Hash(query),
				searchSessionId,
				answerString: '',
				answerLength: 0,
				answerWordCount: 0,
				answerFormat: null,
				answerCardType: AnswerCardType.BOOKMARK_KNOWLEDGE_CARD,
				sources: [],
				followUps: [],
				errorState: null,
				errorStateSuggestedQueries: [],
				bookmarkId,
				bookmarkUrl: bookmarkId,
				extraAttributes: {
					bookmarkId,
					bookmarkUrl: bookmarkId,
					isFullPageSearch: true,
					bookmarkState,
					...(fg('kd_fix_sain_mau_instrumentation') ? { requestId } : {}),
				},
				apiSource: 'xp-knowledge-discovery',
				workspaceId: '',
				sessionId: searchSessionId,
				brand,
				isHello: isHello(cloudId),
			}),
			[bookmarkId, brand, cloudId, orgId, query, searchSessionId, source, bookmarkState, requestId],
		);

		useEffect(
			() => {
				const fmpLoadTime = Date.now() - initialLoadTs;
				trackAIInteractionInit();
				fireSAINUsageEvent({ commonAttributes: bookmarkAttributes });
				fireAnalyticsEvent(
					onAIAnswerResultShown({
						...bookmarkAttributes,
						isStreamed: false,
						fmpLoadTime,
					}),
				);
				SAINFeatureControl.triggerNonPrimaryClickTrackingExposure();
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[],
		);

		useEffect(() => {
			const getSmartLinkDescription = async () => {
				try {
					if (!bookmarkDescription) {
						const objectResolver = await fetchObjectResolver(url);
						setBookmarkDescription(objectResolver?.summary ?? '');
					}
				} catch (error) {
					logger.error({ error, functionName: 'getSmartLinkDescription' });
				}
			};

			getSmartLinkDescription();
		}, [bookmarkDescription, url]);

		const sanitizeUrl = () => {
			let pattern = /^(http|https):\/\//;
			let result = url;

			if (!pattern.test(url)) {
				result = `https://${url}`;
			}

			return result;
		};

		const onSearchPinOpened = (isAuxClick = false, isContextMenu = false) => {
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...bookmarkAttributes,
					location: 'bookmarkKnowledgeCard',
					isAuxClick,
					isContextMenu,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.BOOKMARK_CLICKED,
				commonAttributes: bookmarkAttributes,
			});
		};

		const openAddPinModal = (e: React.MouseEvent<HTMLElement>) => {
			e.stopPropagation();
			setShowAddBookmarkModal(true);
		};

		const bookmarkTitle = title ?? query;
		const isSuggestedBookmark = bookmarkState === BookmarkState.SUGGESTED;

		return (
			<Fragment>
				<Box
					ref={cardRef}
					onAuxClick={(e: React.MouseEvent) => {
						if (e.button !== 1) {
							return;
						}
						onSearchPinOpened(true);
					}}
					onContextMenu={() => onSearchPinOpened(false, true)}
				>
					<KnowledgeCard
						name="smart-answers-bookmark-knowledge-card"
						size="full"
						containerStyles={styles.container}
					>
						<BookmarkInvisibleLink
							url={sanitizeUrl()}
							onClick={() => onSearchPinOpened(false)}
							label={bookmarkTitle}
							onMouseEnter={() => setHovered(true)}
							onMouseLeave={() => setHovered(false)}
						/>

						<Stack space="space.150" grow="fill">
							<Inline alignBlock="center" spread="space-between">
								<Inline alignBlock="center" space="space.100">
									<BookOpenIcon label="" size="small" color={token('color.icon.subtle')} />
									<Text color="color.text.subtle">
										{isSuggestedBookmark
											? formatMessage(messages.pin_title_suggested)
											: formatMessage(messages.pin_title)}
									</Text>
									<Tooltip
										content={
											isSuggestedBookmark
												? formatMessage(messages.pinSuggestedTooltip)
												: formatMessage(messages.pinCompanyAddedTooltip)
										}
										position="bottom"
									>
										{(tooltipProps) => (
											<Box {...tooltipProps}>
												<StatusInformationIcon
													label=""
													size="small"
													color={token('color.icon.subtle')}
												/>
											</Box>
										)}
									</Tooltip>
								</Inline>
								{hasBookmarkCreationAccess && (
									<Button
										spacing="compact"
										onClick={openAddPinModal}
										testId="admin-create-bookmark-button"
									>
										{formatMessage(messages.add_another_pin_button_label)}
									</Button>
								)}
							</Inline>

							<Link
								href={sanitizeUrl()}
								appearance="subtle"
								onClick={() => onSearchPinOpened(false)}
							>
								<Box xcss={hovered ? styles.headingUnderline : undefined}>
									<Text size="large" color="color.link" weight="semibold">
										{bookmarkTitle}
									</Text>
								</Box>
							</Link>

							<Text>{bookmarkDescription}</Text>

							<AIDisclaimerAndFeedback showAIUsageDisclaimer={false} showFeedbackButtons={true} />
						</Stack>
					</KnowledgeCard>
				</Box>

				{showAddBookmarkModal && (
					<AddPinModal
						handleClose={() => setShowAddBookmarkModal(false)}
						orgId={orgId}
						cloudId={cloudId}
						hasAdminhubAccess={hasAdminhubAccess}
						workspaceId={workspaceId}
						product={product}
					/>
				)}

				{showFeedbackModal && (
					<BookmarkFeedbackModal
						setShowDisplayFlag={setShowFeedbackFlag}
						setShowFeedbackModal={setShowFeedbackModal}
						userDetails={userDetails}
						query={query}
						url={url}
					/>
				)}

				{showFeedbackFlag && (
					<FlagGroup onDismissed={() => setShowFeedbackFlag(false)}>
						<Suspense fallback={<></>}>
							<AkFeedbackFlag
								locale={locale}
								description={formatMessage(messages.pinFeedbackSubmittedFlagMessage)}
							/>
						</Suspense>
					</FlagGroup>
				)}
			</Fragment>
		);
	},
);
